import { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@clerk/react';
import {
  Container,
  Paper,
  Text,
  Group,
  Stack,
  Badge,
  Loader,
  Alert,
  Button,
  Avatar,
  TextInput,
  ActionIcon,
  ScrollArea,
  Modal
} from '@mantine/core';
import {
  IconArrowLeft,
  IconSend,
  IconAlertCircle,
  IconUser,
  IconCrown,
  IconRefresh,
  IconRosetteDiscountCheck
} from '@tabler/icons-react';
import { useSession, useApiClient, useLiveQuery, useRealtime } from '../api';
import UserActionsMenu from '../components/safety/UserActionsMenu';

// Human-readable text for the can-message reason enums (contract §4.5)
const CAN_MESSAGE_REASONS = {
  allowed: 'Allowed',
  admin: 'Admin',
  subscribed: 'Active subscription',
  subscription_required: 'Subscription required to message sellers',
  self: 'You cannot message yourself',
  blocked: "You can't message this user.",
};

const ConversationPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const api = useApiClient();
  const { me, isAdmin } = useSession();
  const { status: realtimeStatus } = useRealtime();

  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [canMessage, setCanMessage] = useState(false);
  const [canMessageReason, setCanMessageReason] = useState('');
  const [canMessageReasonCode, setCanMessageReasonCode] = useState('');
  // Bumped after a block/unblock so the can-message check re-runs.
  const [canMessageVersion, setCanMessageVersion] = useState(0);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const messagesEndRef = useRef(null);

  const otherUserId = parseInt(userId, 10);
  const myUserId = me?.user_id;

  // Conversation, kept live on the caller's messages topic:
  // - message.new for THIS conversation -> refetch (the GET also auto-marks
  //   incoming messages read server-side, which emits message.read to the sender)
  // - message.new for another conversation -> suppress the refetch
  // - message.read -> refetch so sent-message "Read" receipts update
  const {
    data: conversationData,
    loading,
    error: conversationError,
    refetch: refetchConversation
  } = useLiveQuery(
    `/api/v1/messages/conversations/${otherUserId}`,
    { page: 1, limit: 50 },
    {
      enabled: !!isSignedIn && Number.isFinite(otherUserId),
      topics: [me && `user.${me.clerk_user_id}.messages`],
      onEvent: (evt) => {
        if (evt.type === 'message.new') {
          const message = evt.data?.message;
          if (!message || myUserId == null) return undefined;
          const partnerId = message.sender_id === myUserId ? message.recipient_id : message.sender_id;
          if (partnerId !== otherUserId) return false; // not this conversation
        }
        return undefined; // refetch
      },
    }
  );

  const messages = useMemo(() => conversationData?.items || [], [conversationData]);
  const otherUser = conversationData?.other_user || null;

  // Check messaging permissions
  useEffect(() => {
    if (!isSignedIn || !Number.isFinite(otherUserId)) return;

    let cancelled = false;
    api.get(`/api/v1/messages/can-message/${otherUserId}`)
      .then((response) => {
        if (cancelled) return;
        setCanMessage(response.allowed);
        setCanMessageReason(CAN_MESSAGE_REASONS[response.reason] || response.reason);
        setCanMessageReasonCode(response.reason);
        if (!response.allowed && response.reason === 'subscription_required') {
          setShowUpgradeModal(true);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error checking permissions:', err);
        setCanMessage(false);
      });
    return () => { cancelled = true; };
  }, [isSignedIn, otherUserId, api, canMessageVersion]);

  // Redirect if not signed in — only once Clerk has settled, otherwise
  // signed-in users deep-linking here bounce home on cold load.
  useEffect(() => {
    if (isLoaded && !isSignedIn) {
      navigate('/');
    }
  }, [isLoaded, isSignedIn, navigate]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!newMessage.trim() || sending) return;

    // Check permissions again before sending
    if (!canMessage && !isAdmin) {
      setShowUpgradeModal(true);
      return;
    }

    try {
      setSending(true);
      setError(null);

      await api.post('/api/v1/messages', {
        recipient_id: otherUserId,
        content: newMessage.trim(),
      });

      setNewMessage('');
      // The sender also receives message.new on their own topic, but refetch
      // immediately so the message appears without waiting for the event.
      refetchConversation();
    } catch (err) {
      console.error('Error sending message:', err);
      setError(err?.message || 'Failed to send message');

      // Subscription gate surfaces as 403 forbidden
      if (err?.code === 'forbidden') {
        setShowUpgradeModal(true);
      }
    } finally {
      setSending(false);
    }
  };

  const formatMessageTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  if (!isLoaded || loading) {
    return (
      <Container size="md" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <Loader size="lg" />
        </div>
      </Container>
    );
  }

  return (
    <Container size="md" py="xl">
      <Stack gap="lg" style={{ height: 'calc(100vh - 200px)' }}>
        {/* Header */}
        <Paper shadow="sm" p="md" radius="md" withBorder>
          <Group justify="space-between">
            <Group gap="sm">
              <ActionIcon
                onClick={() => navigate('/messages')}
                variant="light"
                size="lg"
              >
                <IconArrowLeft size="1.2rem" />
              </ActionIcon>
              <Avatar size="md" color="pink" src={otherUser?.avatar_url}>
                <IconUser size="1.5rem" />
              </Avatar>
              <div>
                <Group gap="xs">
                  <Text fw={600}>{otherUser?.display_name}</Text>
                  {otherUser?.is_seller && (
                    <Badge color="pink" variant="light" size="sm">
                      Princess
                    </Badge>
                  )}
                  {otherUser?.identity_verified && (
                    <Badge
                      color="blue"
                      variant="light"
                      size="sm"
                      leftSection={<IconRosetteDiscountCheck size="0.8rem" />}
                    >
                      Verified buyer
                    </Badge>
                  )}
                </Group>
              </div>
              <UserActionsMenu
                userId={otherUserId}
                displayName={otherUser?.display_name}
                onBlockChange={() => setCanMessageVersion((v) => v + 1)}
              />
            </Group>
            <Group gap="xs">
              {realtimeStatus === 'open' && (
                <Badge color="green" variant="dot" size="sm">
                  Live
                </Badge>
              )}
              <ActionIcon
                onClick={refetchConversation}
                variant="light"
                size="lg"
                disabled={loading}
              >
                <IconRefresh size="1.2rem" />
              </ActionIcon>
            </Group>
          </Group>
        </Paper>

        {(error || conversationError) && (
          <Alert icon={<IconAlertCircle size="1rem" />} color="red">
            {error || conversationError?.message}
          </Alert>
        )}

        {/* Messages */}
        <Paper shadow="sm" p="md" radius="md" withBorder style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <ScrollArea style={{ flex: 1 }} offsetScrollbars>
            <Stack gap="md" p="sm">
              {messages.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}>
                  <Text c="dimmed">No messages yet. Start the conversation!</Text>
                </div>
              ) : (
                messages.map((message) => {
                  const isSent = message.sender_id === myUserId;
                  return (
                    <div
                      key={message.id}
                      style={{
                        display: 'flex',
                        justifyContent: isSent ? 'flex-end' : 'flex-start',
                        marginBottom: '0.5rem'
                      }}
                    >
                      <div
                        style={{
                          maxWidth: '70%',
                          padding: '0.75rem 1rem',
                          borderRadius: '12px',
                          backgroundColor: isSent ? '#228be6' : '#f1f3f5',
                          color: isSent ? 'white' : 'inherit'
                        }}
                      >
                        <Text size="sm" style={{ wordBreak: 'break-word' }}>
                          {message.content}
                        </Text>
                        <Text
                          size="xs"
                          c={isSent ? 'rgba(255,255,255,0.7)' : 'dimmed'}
                          mt={4}
                        >
                          {formatMessageTime(message.created_at)}
                          {isSent && message.is_read && ' • Read'}
                        </Text>
                      </div>
                    </div>
                  );
                })
              )}
              <div ref={messagesEndRef} />
            </Stack>
          </ScrollArea>
        </Paper>

        {/* Message Input */}
        <Paper shadow="sm" p="md" radius="md" withBorder>
          {canMessage || isAdmin ? (
            <Group gap="sm">
              <TextInput
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.currentTarget.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                style={{ flex: 1 }}
                disabled={sending}
              />
              <ActionIcon
                onClick={sendMessage}
                variant="filled"
                size="lg"
                disabled={!newMessage.trim() || sending}
                loading={sending}
              >
                <IconSend size="1.2rem" />
              </ActionIcon>
            </Group>
          ) : canMessageReasonCode === 'blocked' ? (
            <Alert color="gray" variant="light">
              <Text fw={500}>{CAN_MESSAGE_REASONS.blocked}</Text>
              <Text size="sm" c="dimmed">
                Messaging is unavailable while a block is in place.
              </Text>
            </Alert>
          ) : (
            <Alert color="yellow" variant="light">
              <Group gap="sm">
                <IconCrown size="1.2rem" />
                <div>
                  <Text fw={500}>Premium Feature</Text>
                  <Text size="sm">{canMessageReason || 'You need a subscription to message this user.'}</Text>
                </div>
              </Group>
              <Button
                mt="sm"
                variant="gradient"
                gradient={{ from: 'pink', to: 'grape' }}
                leftSection={<IconCrown size="1rem" />}
                onClick={() => navigate('/subscription')}
              >
                Upgrade to PayPiggiePro
              </Button>
            </Alert>
          )}
        </Paper>
      </Stack>

      {/* Upgrade Modal */}
      <Modal
        opened={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        title="Upgrade to PayPiggiePro"
        centered
      >
        <Stack gap="md">
          <Group gap="sm">
            <IconCrown size="2rem" color="var(--mantine-color-yellow-6)" />
            <div>
              <Text fw={500}>Premium Feature</Text>
              <Text size="sm" c="dimmed">
                Messaging princesses is only available for PayPiggiePro subscribers
              </Text>
            </div>
          </Group>
          <Alert color="pink" variant="light">
            <Text size="sm">
              Upgrade to PayPiggiePro to unlock:
            </Text>
            <ul style={{ marginTop: '0.5rem', marginBottom: 0 }}>
              <li>Direct messaging with all princesses</li>
              <li>Priority customer support</li>
              <li>Exclusive deals and discounts</li>
              <li>Early access to new products</li>
            </ul>
          </Alert>
          <Group justify="flex-end" gap="sm">
            <Button
              variant="light"
              onClick={() => setShowUpgradeModal(false)}
            >
              Maybe Later
            </Button>
            <Button
              variant="gradient"
              gradient={{ from: 'pink', to: 'grape' }}
              leftSection={<IconCrown size="1rem" />}
              onClick={() => navigate('/subscription')}
            >
              Upgrade Now - $25/month
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
};

export default ConversationPage;
